/**
 * Ruído conhecido de erro JS — controle de fluxo do próprio app ou de terceiro,
 * NÃO falha de aplicação.
 *
 * Vive num módulo só seu porque agora há DOIS consumidores do mesmo invariante:
 * o `OrderFlowErrorHandler` (telemetria do funil) e o gate de envio ao Sentry.
 * Duplicar a lista faria os dois divergirem no primeiro ruído novo — e aí um
 * painel ficaria limpo enquanto o outro afoga.
 *
 * Medido no 1º dia de produção da instrumentação: destes, dois sozinhos eram
 * ~15% de TODO o tráfego de telemetria. Emiti-los afoga o painel e faz um erro
 * NOVO (o sinal que o monitor existe para pegar) desaparecer no ruído.
 */

/**
 * Mensagens que casam EXATAMENTE (nunca por substring: `includes` aqui pegaria
 * um erro nosso legítimo que só cite a mesma palavra).
 *
 *  - `not logged`: `AuthenticationService` rejeita a promise com esta string
 *    para todo visitante não-logado (a maioria). É fluxo, não falha.
 *  - `overlay does not exist`: `@ionic/core` rejeita `dismiss()` quando o
 *    overlay já foi fechado ou a rota trocou antes — corrida de ciclo de vida,
 *    sem impacto funcional. Era o 2º erro mais comum (~20k/dia, 14k devices).
 */
export const NOISE_MESSAGES: ReadonlyArray<string> = [
  'not logged',
  'overlay does not exist',
];

/**
 * Padrões casados contra o TOPO DO STACK, não contra a mensagem: a mensagem do
 * OneSignal é só "Timeout", que sozinha é ambígua e suprimiria timeouts nossos
 * legítimos. Quem identifica o terceiro é o frame.
 */
export const NOISE_STACK_PATTERNS: ReadonlyArray<RegExp> = [
  /onesignal/i,
  // In-app browser do Instagram/Facebook: o webview injeta scripts próprios na
  // página (`iabjs://navigation_performance_logger_android` e afins) e os erros
  // deles chegam nos nossos handlers globais como se fossem do app. Não há um
  // frame nosso no stack e não há correção possível do nosso lado.
  // Assinatura vista em produção (28/08/2026, print de cliente no checkout):
  //   Uncaught Error: Error invoking postMessage: Java exception was raised
  //   during method invocation — source iabjs://navigation_performance_logger_android
  // Casado pelo ESQUEMA da URL, não pela mensagem: "postMessage" sozinho
  // suprimiria erro nosso legítimo de comunicação com iframe (domínio próprio).
  // SEM âncora `^`, ao contrário do gêmeo em THIRD_PARTY_DENY_URLS: aqui o
  // alvo é o `stackHead` (3 primeiras linhas do stack unidas por ` | `), cuja
  // primeira linha é `Error: <mensagem>` — a URL só aparece no meio.
  /iabjs:\/\//i,
];

/**
 * Hosts de terceiro cujos erros não são nossos e não temos como corrigir.
 * Usado como `denyUrls` do SDK — o filtro mais barato que existe, porque nem
 * chega a montar o evento.
 */
export const THIRD_PARTY_DENY_URLS: ReadonlyArray<RegExp> = [
  /cdn\.onesignal\.com/i,
  /connect\.facebook\.net/i,
  /googletagmanager\.com/i,
  /googlesyndication\.com/i,
  /google-analytics\.com/i,
  /doubleclick\.net/i,
  // Extensões de browser: o stack aponta para um script que não publicamos.
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
  /^safari-(web-)?extension:\/\//i,
  // Scripts injetados pelo in-app browser do Instagram/Facebook — mesmo motivo
  // do padrão `iabjs:` em NOISE_STACK_PATTERNS, aqui na barreira mais barata
  // (o SDK nem monta o evento).
  /^iabjs:\/\//i,
];

/** É ruído conhecido? `message` casa exato; `stackHead` casa por padrão. */
export function ehRuido(message: string, stackHead: string): boolean {
  if (NOISE_MESSAGES.indexOf(message) !== -1) return true;
  for (const re of NOISE_STACK_PATTERNS) {
    if (re.test(stackHead)) return true;
  }
  return false;
}
